import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UniversityManagerLayout from "../../components/layout/UniversityManagerLayout";
import UMClubList from "../../features/university-manager/UMClubList";
import UMClubForm from "../../features/university-manager/UMClubForm";
import UMClubMembers from "../../features/university-manager/UMClubMembers";
import UMClubEvents from "../../features/university-manager/UMClubEvents";
import ClubDetailModal from "../../features/clubs/ClubDetailModal";
import PermissionGuard from "../../components/common/PermissionGuard";
import { useTenant } from "../../context/TenantContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import { useNotification } from "../../context/NotificationContext";
import umService from "../../services/university-admin/umService";

const UMClubs = () => {
  const { t, i18n } = useTranslation("clubs");
  const tenantCtx = useTenant();
  const { can } = useUniAdminAuth();
  const { showSuccess, showError } = useNotification();

  const [showForm, setShowForm] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [viewingClub, setViewingClub] = useState(null);
  const [managingMembers, setManagingMembers] = useState(null);
  const [managingEvents, setManagingEvents] = useState(null);
  const [clubEvents, setClubEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  const normalizedViewingClub = useMemo(() => {
    if (!viewingClub) return null;
    const isTurkish = i18n.language?.toLowerCase().startsWith("tr");
    return {
      ...viewingClub,
      campusNameEn: isTurkish
        ? viewingClub.campusNameLocal || viewingClub.campusNameEn
        : viewingClub.campusNameEn,
      universityNameEn: isTurkish
        ? viewingClub.universityNameLocal || viewingClub.universityNameEn
        : viewingClub.universityNameEn,
      name: viewingClub.nameEn || viewingClub.name,
      categories: Array.isArray(viewingClub.categories)
        ? viewingClub.categories.map((category, index) =>
            typeof category === "string"
              ? { id: `${viewingClub.id}-${index}`, name: category }
              : {
                  ...category,
                  name: isTurkish
                    ? category.nameLocal || category.name
                    : category.nameEn || category.name,
                },
          )
        : [],
    };
  }, [viewingClub, i18n.language]);

  const handleCreate = () => {
    setEditingClub(null);
    setShowForm(true);
  };

  const handleEdit = async (club) => {
    try {
      setLoading(true);
      const response = await umService.getClubById(club.id, tenantCtx);
      if (response.success) {
        setEditingClub(response.data);
        setShowForm(true);
      } else {
        setEditingClub(club);
        setShowForm(true);
      }
    } catch (error) {
      console.error("Error fetching club details:", error);
      setEditingClub(club);
      setShowForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleView = async (club) => {
    try {
      setLoading(true);
      const response = await umService.getClubById(club.id, tenantCtx);
      if (response.success) {
        setViewingClub(response.data);
      } else {
        setViewingClub(club);
      }
    } catch (error) {
      console.error("Error fetching club details for view:", error);
      setViewingClub(club);
    } finally {
      setLoading(false);
    }
  };

  const handleFormSubmit = async (formData) => {
    try {
      setLoading(true);
      const response = editingClub
        ? await umService.updateClub({
            id: editingClub.id,
            ...tenantCtx,
            data: formData,
          })
        : await umService.createClub({ ...tenantCtx, data: formData });

      if (response.success) {
        showSuccess(
          editingClub
            ? t("messages.updateSuccess")
            : t("messages.createSuccess"),
        );
        setShowForm(false);
        setEditingClub(null);
        setRefreshTrigger((prev) => prev + 1);
      } else {
        showError(t("messages.loadFailed"));
      }
    } catch (error) {
      console.error("Form submission error:", error);
      showError(t("messages.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  const handleFormCancel = () => {
    setShowForm(false);
    setEditingClub(null);
  };

  const handleRefresh = () => {
    setRefreshTrigger((prev) => prev + 1);
  };

  const openManageEvents = async (club) => {
    const response = await umService.getEvents({
      clubId: club.id,
      page: 0,
      size: 200,
    });
    setClubEvents(response.success ? response.data.content : []);
    setManagingEvents(club);
  };

  return (
    <UniversityManagerLayout>
      <PermissionGuard allow={can(PERMISSIONS.club.list)}>
      <div className="clubs-page">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1 fw-bold text-dark">{t("management")}</h1>
            <p className="text-muted mb-0">{t("managementSubtitle")}</p>
          </div>
          <div className="d-flex gap-2">
            {can(PERMISSIONS.club.listExport) && (
              <button className="btn btn-outline-primary" onClick={async () => {
                const res = await umService.exportAllClubsReport();
                if (res.success) showSuccess(t("messages.exportSuccess", "Report exported."));
                else showError(t("messages.exportFailed", "Failed to export report."));
              }}>
                <i className="fas fa-file-pdf me-2"></i>
                {t("exportReport")}
              </button>
            )}
            {can(PERMISSIONS.club.create) && (
              <button className="btn btn-primary" onClick={handleCreate}>
                <i className="fas fa-plus me-2"></i>
                {t("addClub")}
              </button>
            )}
          </div>
        </div>

        {showForm && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-xl">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i
                      className={`fas ${editingClub ? "fa-edit" : "fa-plus"} me-2`}
                    ></i>
                    {editingClub ? t("editClub") : t("addNewClub")}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={handleFormCancel}
                    disabled={loading}
                  ></button>
                </div>
                <div className="modal-body">
                  <UMClubForm
                    club={editingClub}
                    onSubmit={handleFormSubmit}
                    onCancel={handleFormCancel}
                    loading={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {normalizedViewingClub && (
          <ClubDetailModal
            club={normalizedViewingClub}
            onClose={() => setViewingClub(null)}
            onEdit={handleEdit}
            onManageMembers={setManagingMembers}
            onManageEvents={openManageEvents}
          />
        )}

        {managingMembers && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-users me-2"></i>
                    {t("manageMembersTitle", {
                      name: managingMembers.nameEn || managingMembers.name,
                    })}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setManagingMembers(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <UMClubMembers
                    club={managingMembers}
                    tenantCtx={tenantCtx}
                    refreshClub={handleRefresh}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {managingEvents && (
          <div
            className="modal show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
          >
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">
                    <i className="fas fa-calendar-alt me-2"></i>
                    {t("manageEventsTitle", {
                      name: managingEvents.nameEn || managingEvents.name,
                    })}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setManagingEvents(null)}
                  ></button>
                </div>
                <div className="modal-body">
                  <UMClubEvents
                    club={managingEvents}
                    events={clubEvents}
                    onClose={() => setManagingEvents(null)}
                    onRefresh={() => {
                      handleRefresh();
                      openManageEvents(managingEvents);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        <UMClubList
          onCreate={handleCreate}
          onEdit={handleEdit}
          onView={handleView}
          onManageMembers={setManagingMembers}
          onManageEvents={openManageEvents}
          refreshTrigger={refreshTrigger}
        />
      </div>
      </PermissionGuard>
    </UniversityManagerLayout>
  );
};

export default UMClubs;
